import {HttpClient} from '@angular/common/http';
import {Injectable, Injector} from '@angular/core';
import {EMPTY, NEVER, Observable, race, Subject, timer} from 'rxjs';
import {
    distinctUntilChanged,
    map,
    repeat,
    retry,
    startWith,
    switchMap,
    switchMapTo,
    takeUntil,
    tap
} from 'rxjs/operators';
import {UserStatus} from '../../enums/user-status';
import {User} from '../../models/user';
import {OnboardingSubmitConfirmModalComponent} from '../../pages/onboarding-page/onboarding-submit-confirm-modal';
import {OnboardingUser} from '../../pages/onboarding-page/onboarding-user';
import {AppConfigService} from '../../services/config.service';
import {UserService} from '../../services/user.service';
import {destroy$} from '../destroyable';
import {DynamicComponentRef} from '../dynamic-component';
import {ModalService} from '../modal';

const ONBOARDING_STATUS_PING_TIMEOUT = 1000 * 60; // 1 minute

@Injectable()
export class OnboardingUpdatesPingService {

    public readonly destroy$ = destroy$();

    private readonly currentUserUrl: string = this.appConfigService.api['currentUser'];

    private alertRef: DynamicComponentRef<OnboardingSubmitConfirmModalComponent>;

    private readonly forcePing$ = new Subject<void>();

    constructor(
        private userService: UserService,
        private appConfigService: AppConfigService,
        private httpClient: HttpClient,
        private modalService: ModalService,
        private injector: Injector
    ) {
        this.startOnboardingStatusPing();
    }

    public forcePing(): void {
        this.forcePing$.next();
    }

    private startOnboardingStatusPing(): void {
        const ping$ = this.httpClient.get<OnboardingUser>(this.currentUserUrl);

        const getPeriodicalTrigger = () =>
            this.userService.user && this.userService.user.status === UserStatus.PENDING
                ? timer(ONBOARDING_STATUS_PING_TIMEOUT)
                : NEVER;

        const continuousPing = () => race(
            getPeriodicalTrigger(),
            this.forcePing$
        ).pipe(
            switchMapTo(ping$),
            retry(),
            repeat()
        );

        this.userService.user$.pipe(
            switchMap(user => user
                ? continuousPing().pipe(
                    startWith(user)
                )
                : EMPTY
            ),
            map(user => user.status),
            distinctUntilChanged(),
            switchMap(status => this.refreshUserIfStatusChanged(status)),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private refreshUserIfStatusChanged(status: UserStatus): Observable<User | never> {
        const isStatusChanged = this.userService.user && this.userService.user.status !== status;

        return isStatusChanged
            ? this.userService.fetchCurrentUser().pipe(
                tap(() => this.showCurrentStatusAlert())
            )
            : EMPTY;
    }

    private showCurrentStatusAlert(): void {
        if (!this.alertRef) {
            this.alertRef = this.modalService.open(OnboardingSubmitConfirmModalComponent, {
                injector:    this.injector,
                destroyWhen: this.destroy$,
                onDestroy:   () => this.alertRef = null
            });
        }
    }

}
